import { getAuthUser, getAuthToken, getAuthEmail } from "./cookieService";

const safeLocalStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const safeLocalStorageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage write errors in restricted contexts.
  }
};

export const localStorageSetter = (key, value) => {
  safeLocalStorageSet(key, JSON.stringify(value));
};

export const localStorageGetter = (key) => {
  const raw = safeLocalStorageGet(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Get user details from cookies (for cross-subdomain auth)
 * @returns {import("./cookieService").UserDetails | null}
 */
export const getUserDetails = () => {
  // First check cookies (for cross-subdomain auth)
  const cookieUser = getAuthUser();
  if (cookieUser) return cookieUser;

  // Fallback to localStorage for iframe contexts where third-party cookie
  // writes may be blocked or delayed.
  try {
    const user = safeLocalStorageGet("user");
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

/**
 * Checks if the current user's auth token is still valid (exists and not expired)
 * @returns {boolean}
 */
export const isTokenValid = () => {
  const userDetails = getUserDetails();
  if (!userDetails || !userDetails.token) return false;

  const { expiresIn } = userDetails;
  if (!expiresIn) return false;

  return new Date(expiresIn) > new Date();
};

export const getUnAuthenticatedUserDetails = () => {
  try {
    const user = safeLocalStorageGet("unAuthenticatedUserDetails");
    if (!user) return null;
    return JSON.parse(user);
  } catch {
    return null;
  }
};

export const resetTimer = () => {
  safeLocalStorageRemove("startTime");
  safeLocalStorageRemove("isActive");
};

/**
 * Get auth token from cookies
 * @returns {string | null}
 */
export const getToken = () => {
  // First check cookies (for cross-subdomain auth)
  const cookieToken = getAuthToken();
  if (cookieToken) return cookieToken;

  // Fallback to localStorage in restricted iframe cookie environments.
  return safeLocalStorageGet("token") || null;
};

/**
 * Get user email from cookies
 * @returns {string | null}
 */
export const getEmail = () => {
  // First check cookies (for cross-subdomain auth)
  const cookieEmail = getAuthEmail();
  if (cookieEmail) return cookieEmail;

  try {
    const user = safeLocalStorageGet("user");
    if (!user) return null;
    const parsed = JSON.parse(user);
    return parsed?.email || safeLocalStorageGet("email");
  } catch {
    return safeLocalStorageGet("email");
  }
};

export const getConnectionId = () => {
  const connectionId = safeLocalStorageGet("connectionId");
  return connectionId ?? "";
};

export const getProjectorConnectionId = () => {
  const connectionId = safeLocalStorageGet("projectorConnectionId");
  return connectionId ?? "";
};

export const getPresentationId = () => {
  const presentationId = safeLocalStorageGet("presentationId");
  return presentationId ?? "";
};

export const beginViewerJourney = () => {
  safeLocalStorageSet("hasViewerJourney", true);
};

export const stopViewerJourney = () => {
  safeLocalStorageSet("hasViewerJourney", false);
};

export const hasViewerJourneyBegan = () => {
  try {
    return JSON.parse(safeLocalStorageGet("hasViewerJourney") ?? false);
  } catch {
    return false;
  }
};

export const getUnlockedSlides = () => {
  try {
    return JSON.parse(safeLocalStorageGet("unlockedSlides")) ?? [];
  } catch {
    return [];
  }
};
export const getViewersJourney = () => {
  try {
    return JSON.parse(safeLocalStorageGet("viewersJourney")) ?? [];
  } catch {
    return [];
  }
};

export const setUnlockedSlides = (id, docId, slideNumber) => {
  //NAVIGATING ACCORDING TO PRESENTER'S MOVEMENT

  // let slides = getUnlockedSlides();
  // let lastEntry = slides?.[slides?.length - 1];
  // if (lastEntry?.docId === docId && lastEntry?.slideNumber === slideNumber) {
  //   return;
  // }
  // slides.push({ id, docId, slideNumber: slideNumber ?? 0 });
  // let newSlides = [...new Set(slides)];
  // localStorage.setItem("unlockedSlides", JSON.stringify(newSlides));
  // if (!hasViewerJourneyBegan()) {
  //   setViewersJourney({ docId, slideNumber: slideNumber ?? 0 });
  // }

  ////////////////////

  //SORT NAVIGATION ACCORDING TO HERACHY OF PLAYLIST

  let slides = getUnlockedSlides();
  const isExist = slides.filter((slide) => {
    return slide?.docId === docId && slide?.slideNumber === slideNumber;
  });

  if (isExist?.length > 0) return;
  slides.push({ id, docId, slideNumber: slideNumber ?? 0 });

  let newSlides = slides?.sort((a, b) => {
    if (a.docId < b.docId) return -1;
    if (a.docId > b.docId) return 1;
    if (a.slideNumber < b.slideNumber) return -1;
    if (a.slideNumber > b.slideNumber) return 1;
    return 0;
  });
  safeLocalStorageSet("unlockedSlides", JSON.stringify(newSlides));
  if (!hasViewerJourneyBegan()) {
    setViewersJourney({ docId, slideNumber: slideNumber ?? 0 });
  }

  /////
};

export const setViewersJourney = (slide) => {
  let slides = getViewersJourney();
  slides.push(slide);
  slides = Array.from(new Set(slides.map((item) => JSON.stringify(item)))).map(
    (str) => JSON.parse(str),
  );
  safeLocalStorageSet("viewersJourney", JSON.stringify(slides));
};

export const resetUnlockedSlides = () => {
  safeLocalStorageSet("unlockedSlides", JSON.stringify([]));

  safeLocalStorageSet("viewersJourney", JSON.stringify([]));
  stopViewerJourney();
};

export const setPresenterActiveDocumentId = (id) => {
  safeLocalStorageSet("presenterActiveDocId", id);
};

export const getPresenterActiveDocumentId = () => {
  return safeLocalStorageGet("presenterActiveDocId");
};
